// ✅ src/pages/VehicleV2/VehicleTypeMaintenanceEntry.jsx
// Visual design aligned with ProductionBOMListing.

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import { FaArrowLeft, FaSave, FaUndo } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "http://nissi-india.com/api/maintenanceEntry";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const getLS = (key, fallback = "") => {
  if (typeof window === "undefined") return fallback;
  const val = localStorage.getItem(key);
  return val !== null && val !== undefined ? val : fallback;
};

const VehicleTypeMaintenanceEntry = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branch = getLS("branchType") || getLS("branchtype") || "";
  const userId = getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  const ipAddress = useMemo(() => sessionStorage.getItem("ipAddress") || "", []);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const [vehicleSubTypes, setVehicleSubTypes] = useState([]);
  const [stores, setStores] = useState([]);
  const [vehicleBasis, setVehicleBasis] = useState([]);

  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicleIds, setSelectedVehicleIds] = useState([]);

  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    storeId: "",
    subTypeId: "",
    qty: "",
    unit: "",
    vehicleBasisId: "",
    dateOfReceipt: "",
    specification: "",
    subAssemblyName: "",
    partNumber: "",
    description: "",
    usageLimit: "",
  });

  const fieldClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const labelClass = `block mb-2 text-sm font-medium ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const panelClass = `p-6 rounded-lg ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`;

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const resetForm = () => {
    setForm({
      storeId: "",
      subTypeId: "",
      qty: "",
      unit: "",
      vehicleBasisId: "",
      dateOfReceipt: "",
      specification: "",
      subAssemblyName: "",
      partNumber: "",
      description: "",
      usageLimit: "",
    });
    setVehicles([]);
    setSelectedVehicleIds([]);
  };

  const loadDropdowns = async () => {
    const res = await axios.get(`${API_BASE}/dropdowns`, {
      params: { branchId },
      headers: commonHeaders,
    });

    if (res.data?.success) {
      setVehicleSubTypes(Array.isArray(res.data?.data?.vehicleSubTypes) ? res.data.data.vehicleSubTypes : []);
      setStores(Array.isArray(res.data?.data?.stores) ? res.data.data.stores : []);
      setVehicleBasis(Array.isArray(res.data?.data?.vehicleBasis) ? res.data.data.vehicleBasis : []);
    } else {
      throw new Error(res.data?.message || "Dropdown load failed");
    }
  };

  const loadVehicles = async () => {
    const subTypeId = form.subTypeId;
    const basisId = form.vehicleBasisId;
    const subAssemblyName = (form.subAssemblyName || "").trim();

    if (!subTypeId || !basisId || !subAssemblyName) {
      setVehicles([]);
      setSelectedVehicleIds([]);
      return;
    }

    const res = await axios.get(`${API_BASE}/vehicles`, {
      params: {
        branchId,
        subTypeId,
        basisId,
        subAssemblyName,
      },
      headers: commonHeaders,
    });

    if (res.data?.success) {
      setVehicles(Array.isArray(res.data?.data) ? res.data.data : []);
      setSelectedVehicleIds([]);
    } else {
      toast.error(res.data?.message || "Vehicle list load failed");
      setVehicles([]);
      setSelectedVehicleIds([]);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await loadDropdowns();
      } catch (e) {
        toast.error(e?.message || "Dropdown load failed");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commonHeaders]);

  useEffect(() => {
    loadVehicles().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.subTypeId, form.vehicleBasisId, form.subAssemblyName]);

  const toggleVehicle = (id) => {
    setSelectedVehicleIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const validate = () => {
    if (!form.storeId) return "Select Store";
    if (!form.subTypeId) return "Select Sub Type";
    if (!form.qty || Number(form.qty) <= 0) return "Enter valid Qty";
    if (!form.vehicleBasisId) return "Select Vehicle Basis";
    if (!form.dateOfReceipt) return "Select Date of Receipt";
    if (!form.subAssemblyName.trim()) return "Enter Sub Assembly";
    if (!form.partNumber.trim()) return "Enter PartNumber/Grade";
    if (!form.description.trim()) return "Enter Description";
    if (!selectedVehicleIds.length) return "Select Equipment List";
    if (form.usageLimit === "" || Number(form.usageLimit) <= 0) return "Enter valid Usage Limit";
    return "";
  };

  const handleSubmit = async () => {
    const msg = validate();
    if (msg) {
      toast.error(msg);
      return;
    }

    try {
      setLoading(true);

      const payload = {
        branchId: toInt(branchId),
        loginUserId: toInt(userId),
        storeId: toInt(form.storeId),
        subTypeId: toInt(form.subTypeId),
        vehicleBasisId: toInt(form.vehicleBasisId),
        subAssemblyName: form.subAssemblyName.trim(),
        partNumber: form.partNumber.trim(),
        qty: Number(form.qty),
        unit: form.unit || "",
        description: form.description.trim(),
        specification: form.specification || "",
        usageLimit: Number(form.usageLimit),
        dateOfReceipt: form.dateOfReceipt || "",
        ipAddress,
        vehicleIds: selectedVehicleIds,
      };

      const res = await axios.post(`${API_BASE}/save`, payload, {
        headers: commonHeaders,
      });

      if (res.data?.success) {
        toast.success(res.data?.message || "Inserted Successfully");
        resetForm();
      } else {
        toast.error(res.data?.message || "Save failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    resetForm();
  };

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">VEHICLE TYPE MAINTENANCE ENTRY</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* ==================== HEADER ==================== */}
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div>
              <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Vehicle Type Maintenance Entry
              </h2>
              <p className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Create a maintenance record and map it to selected equipment.
              </p>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                disabled={loading}
                onClick={handleSubmit}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${
                  loading ? "opacity-60 cursor-not-allowed" : ""
                } ${buttonClass} ${hoverClass} text-white`}
              >
                <FaSave />
                {loading ? "Saving..." : "Submit"}
              </button>

              <button
                disabled={loading}
                onClick={handleReset}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                } ${loading ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaUndo />
                Reset
              </button>
            </div>
          </div>

          {/* ==================== FORM ==================== */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* LEFT */}
            <div className={panelClass}>
              <h3 className={`text-lg font-semibold mb-4 ${isDark ? "text-white" : "text-blue-900"}`}>
                Store & Receipt Details
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className={labelClass}>
                    Store<span className="text-red-500"> *</span>
                  </label>
                  <select name="storeId" value={form.storeId} onChange={onChange} className={fieldClass}>
                    <option value="">Select</option>
                    {stores.map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className={labelClass}>
                    Sub Type<span className="text-red-500"> *</span>
                  </label>
                  <select name="subTypeId" value={form.subTypeId} onChange={onChange} className={fieldClass}>
                    <option value="">Select</option>
                    {vehicleSubTypes.map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>
                    Qty<span className="text-red-500"> *</span>
                  </label>
                  <input type="number" name="qty" value={form.qty} onChange={onChange} className={fieldClass} />
                </div>

                <div>
                  <label className={labelClass}>Unit</label>
                  <input type="text" name="unit" value={form.unit} onChange={onChange} className={fieldClass} />
                </div>

                <div className="md:col-span-2">
                  <label className={labelClass}>
                    Vehicle Basis<span className="text-red-500"> *</span>
                  </label>
                  <select
                    name="vehicleBasisId"
                    value={form.vehicleBasisId}
                    onChange={onChange}
                    className={fieldClass}
                  >
                    <option value="">Select</option>
                    {vehicleBasis.map((x, idx) => (
                      <option key={x.id ?? idx} value={x.id}>
                        {x.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>
                    Date of Receipt<span className="text-red-500"> *</span>
                  </label>
                  <input
                    type="date"
                    name="dateOfReceipt"
                    value={form.dateOfReceipt}
                    onChange={onChange}
                    className={fieldClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Specification</label>
                  <input
                    type="text"
                    name="specification"
                    value={form.specification}
                    onChange={onChange}
                    className={fieldClass}
                  />
                </div>
              </div>
            </div>

            {/* RIGHT */}
            <div className={panelClass}>
              <h3 className={`text-lg font-semibold mb-4 ${isDark ? "text-white" : "text-blue-900"}`}>
                Part & Equipment Details
              </h3>

              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className={labelClass}>
                    Sub Assembly<span className="text-red-500"> *</span>
                  </label>
                  <input
                    type="text"
                    name="subAssemblyName"
                    value={form.subAssemblyName}
                    onChange={onChange}
                    className={fieldClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    PartNumber Grade<span className="text-red-500"> *</span>
                  </label>
                  <input
                    type="text"
                    name="partNumber"
                    value={form.partNumber}
                    onChange={onChange}
                    className={fieldClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    Description<span className="text-red-500"> *</span>
                  </label>
                  <input
                    type="text"
                    name="description"
                    value={form.description}
                    onChange={onChange}
                    className={fieldClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    Equipment List<span className="text-red-500"> *</span>
                    {selectedVehicleIds.length > 0 && (
                      <span
                        className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${
                          isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                        }`}
                      >
                        {selectedVehicleIds.length} selected
                      </span>
                    )}
                  </label>

                  <div className="overflow-x-auto rounded-lg border shadow-lg">
                    <div
                      className={`h-40 overflow-auto ${
                        isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
                      }`}
                    >
                      {!form.subTypeId || !form.vehicleBasisId || !form.subAssemblyName.trim() ? (
                        <div className={`p-6 text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                          Select Sub Type + Vehicle Basis and enter Sub Assembly to load equipment list.
                        </div>
                      ) : vehicles.length === 0 ? (
                        <div className={`p-6 text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                          {loading ? "Loading..." : "No vehicles found"}
                        </div>
                      ) : (
                        vehicles.map((v, idx) => {
                          const vehicleId =
                            v.id ??
                            v.vehicleId ??
                            v.fld_vehicleid ??
                            v.fld_vehiclemasterid ??
                            idx;

                          return (
                            <label
                              key={vehicleId}
                              className={`flex items-center gap-2 px-4 py-2 cursor-pointer transition-colors ${
                                idx % 2 === 0
                                  ? isDark
                                    ? "bg-gray-800"
                                    : "bg-white"
                                  : isDark
                                  ? "bg-gray-750"
                                  : "bg-gray-50"
                              } ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedVehicleIds.includes(vehicleId)}
                                onChange={() => toggleVehicle(vehicleId)}
                                className="w-4 h-4 cursor-pointer"
                              />
                              <span className={`text-sm ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                                {v.regNo || v.registrationNo || v.fld_regno || "-"}{" "}
                                <span className={isDark ? "text-gray-400" : "text-gray-500"}>
                                  ({v.equipmentSlNo || v.equipmentNo || v.fld_equipmentslno || "-"})
                                </span>
                              </span>
                            </label>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <label className={labelClass}>
                    Usage Limit<span className="text-red-500"> *</span>
                  </label>
                  <input
                    type="number"
                    name="usageLimit"
                    value={form.usageLimit}
                    onChange={onChange}
                    className={fieldClass}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ==================== FOOTER ACTIONS ==================== */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => window.history.back()}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${
                isDark
                  ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                  : "border-gray-300 text-gray-700 hover:bg-white"
              }`}
            >
              <FaArrowLeft />
              Back
            </button>

            <div className="flex gap-2">
              <button
                disabled={loading}
                onClick={handleReset}
                className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                  isDark
                    ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                    : "border-gray-300 text-gray-700 hover:bg-white"
                } ${loading ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                Reset
              </button>

              <button
                disabled={loading}
                onClick={handleSubmit}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  loading ? "opacity-60 cursor-not-allowed" : ""
                } ${buttonClass} ${hoverClass} text-white`}
              >
                <FaSave />
                {loading ? "Saving..." : "Submit"}
              </button>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleTypeMaintenanceEntry;